import { FC, useEffect, useState } from "react"
import { IDocumentCreatorProps } from "../../../@types/documents"
import { LoadingStatus } from "../../../@types/enumsGlobal"
import { DefaultGrid } from "../../../components/grids/default/defaultGrid"
import { PluginWrapper } from "../../../components/plugins"
import { ILotMovementDTO, ILotMovementViewDTO } from "../../../libs/coreapi-dto/accounting/lotMovement"
import useGridFilter from "../../../system/hooks/useGridFilter"
import { useAppContext } from "../../../system/providers/appContextProvider"
import { v4 as uuidv4 } from "uuid";
import { LotMovementGridPluginSettings } from "."
import { LotMovementDataProvider } from "../../../Services/DataProviders/LotMovementDataProvider"
import style from "./styles/LotMovement.module.scss";
import FlexRow from "../../../components/controls/FlexRow"
import { LotMovementPanel } from "./Components/LotMovementPanel"
import SearchModalWindowAntd from "../../../components/modalWindows/SearchModalWindowAntd/SearchModalWindowAntd"
import { DateTime } from "luxon"
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker"



const LotMovementCreatorView: FC<IDocumentCreatorProps<ILotMovementDTO>> = (props) => {
    const appContext = useAppContext()
    const lotMovementDataProvider = new LotMovementDataProvider(appContext.coreApiService)

    const [idLotGlobal,] = useState<string | undefined>(props.idGlobal)
    const [idGoodsGlobal] = useState<string | undefined>(props.idGoodsGlobal)

    const [data, setData] = useState<ILotMovementViewDTO[]>([]);
    const [dataVersion, setDataVersion] = useState(0);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.Completed);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [startFilterAdded, setStartFilterAdded] = useState<boolean>(false)
    const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
    const DefaultDatePeriod = { startDate: DateTime.now().plus({ days: -14 }), endDate: DateTime.now() } as IDatePeriod;
    const startDate = DefaultDatePeriod?.startDate?.toFormat("yyyyMMdd");
    const endDate = DefaultDatePeriod?.endDate?.toFormat("yyyyMMdd");
    const defaultGridFilter: IGridFilter = {
        pageNumber: 1,
        numberPerPage: 15,
        columnFilters:
            [
                {
                    name: "dateOp",
                    value: startDate,
                    operator: "MoreOrEq",
                } as IGridColumnFilter,
                {
                    name: "dateOp",
                    value: endDate,
                    operator: "LessOrEq",
                } as IGridColumnFilter,
            ],
        paramFilter: {}
    }
    
    const [gridFilter, dispatchGridFilter] = useGridFilter(defaultGridFilter);

    useEffect(() => {
        if (!startFilterAdded) {
            setStartFilterAdded(true)
            if (idLotGlobal || idGoodsGlobal) {
                dispatchGridFilter({ type: 'paramFilter', payload: { gridParamFilter: { idLot: idLotGlobal, idGoods: idGoodsGlobal} } })
            }
            return
        }
        fetchData();
    }, [gridFilter, dataVersion, startFilterAdded])

    const fetchData = async () => {
        try {
            setLoadingStatus(LoadingStatus.InProcess);
            lotMovementDataProvider.getView(gridFilter, (data, totalCount) => {
                data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
                setData(data);
                setTotalCount(totalCount);
            });
        } catch {
            setLoadingStatus(LoadingStatus.Failed);
        }
    };
    
    const getStyleRow = (row: IGridRow): string | null => {
        let cell = row.cells.find(c => c.propertyName === 'typeOp');
        if (!cell) return null
        if (cell.value === 'Резерв' || cell.value === 'Оперативный резерв') {
            return style.td
        }
        return null
    };

    const hasActiveFilters = () => {
        if (!gridFilter?.columnFilters) return false;
    
        return gridFilter.columnFilters.some(filter =>
            filter.name !== "deleted"
        );
    };

    const handleSearchToggle = () => {
        setIsSearchModalVisible(!isSearchModalVisible);
    };

    const handleRefresh = () => {
        setDataVersion(prev => prev + 1);
    };

    const handleSearch = (columnFilters: IGridColumnFilter[]) => {
        dispatchGridFilter({ type: 'search', payload: columnFilters });
        handleSearchToggle();
        setDataVersion(prev => prev + 1);
    }

    return <>
        <PluginWrapper>
            <FlexRow>
                <LotMovementPanel
                    dispatchGridFilter={dispatchGridFilter}
                    hasActiveFilters={hasActiveFilters()}
                    onClickOnSearchButton={handleSearchToggle}
                    onClickRefresh={handleRefresh}
                />
            </FlexRow>
            <DefaultGrid
                showQuantity
                searching
                isServerFilter
                getView={(gridFilter, callBack) => lotMovementDataProvider.getView(gridFilter, (data) => {
                    callBack(data)
                })}
                gridId={uuidv4()}
                data={data}
                loadingStatus={loadingStatus}
                setLoadingStatus={setLoadingStatus}
                totalCount={totalCount}
                dataProvider={lotMovementDataProvider}
                filter={gridFilter}
                plugin={LotMovementGridPluginSettings}
                selectedItem={selectedItem}
                getStyleRow={(row) => getStyleRow(row)}
                onSelect={(row) => {
                    setSelectedItem(row)
                }}
                onDoubleClick={(i) => { }}
                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
            />
            {isSearchModalVisible && (
                <SearchModalWindowAntd
                columns={LotMovementGridPluginSettings.columns ?? []}
                defaultFilters={gridFilter?.columnFilters}
                search={handleSearch}
                cancel={handleSearchToggle}
                open={true}
                onCancel={handleSearchToggle}
                />
            )}
        </PluginWrapper>
    </>
}

export default LotMovementCreatorView